import { useState, useCallback, useMemo } from "react";

export type SelectableItem<T extends string | number = string | number> = {
  id: T;
};

export type SectionedData<T extends SelectableItem> = {
  connected?: T[];
  managed?: T[];
};

export function useTableSelection<T extends SelectableItem>(
  data: SectionedData<T> | null,
) {
  const [selectedIds, setSelectedIds] = useState<Array<T["id"]>>([]);

  const toggleSelection = useCallback((id: T["id"]) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((existingId) => existingId !== id) : [...current, id],
    );
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const selectAll = useCallback((items: T[]) => {
    setSelectedIds(items.map((item) => item.id));
  }, []);

  const selectSection = useCallback(
    (section: "connected" | "managed") => {
      if (!data) return;

      const sectionItems = data[section] || [];
      const sectionIds = sectionItems.map((item) => item.id);
      const allSelected = sectionIds.length > 0 && sectionIds.every((id) => selectedIds.includes(id));

      setSelectedIds((current) => {
        if (allSelected) {
          return current.filter((id) => !sectionIds.includes(id));
        }
        return Array.from(new Set([...current, ...sectionIds]));
      });
    },
    [data, selectedIds],
  );

  const allItems = useMemo(() => {
    if (!data) return [];
    return [...(data.connected ?? []), ...(data.managed ?? [])];
  }, [data]);

  const connectedItems = useMemo(() => data?.connected ?? [], [data]);
  const managedItems = useMemo(() => data?.managed ?? [], [data]);

  const isAllSelected = useMemo(
    () => allItems.length > 0 && allItems.every((item) => selectedIds.includes(item.id)),
    [allItems, selectedIds],
  );

  const isConnectedSectionSelected = useMemo(
    () => connectedItems.length > 0 && connectedItems.every((item) => selectedIds.includes(item.id)),
    [connectedItems, selectedIds],
  );

  const isManagedSectionSelected = useMemo(
    () => managedItems.length > 0 && managedItems.every((item) => selectedIds.includes(item.id)),
    [managedItems, selectedIds],
  );

  const isSelected = useCallback(
    (id: T["id"]) => selectedIds.includes(id),
    [selectedIds],
  );

  return {
    selectedIds,
    selectedCount: selectedIds.length,
    toggleSelection,
    clearSelection,
    selectAll,
    selectSection,
    allItems,
    connectedItems,
    managedItems,
    isAllSelected,
    isConnectedSectionSelected,
    isManagedSectionSelected,
    isSelected,
  };
}

